import { Alert, Card, Flex, Modal, Skeleton, Space, Tag, Typography } from 'antd';
import React, { useRef } from 'react';
import ReactMarkdown from 'react-markdown';

import { Button } from '@/atoms/Button';
import {
  AssessmentStepPager,
  type AssessmentFlowStep,
} from '@/components/assessment/AssessmentStepPager';
import {
  CrepeMarkdownField,
  type CrepeMarkdownFieldHandle,
} from '@/components/editor/CrepeMarkdownField';
import { useInterviewGuide } from '@/hooks/useInterviewGuide';
import { downloadMarkdownFile } from '@/utils/assessmentInterviewFlow';
import { type InterviewGuideReviewModel } from '@/utils/interviewGuideReviewParser';

const { Paragraph, Text, Title } = Typography;
const INTERVIEW_GUIDE_FILENAME = '01-interview-guide.md';
const INLINE_MARKDOWN_COMPONENTS = {
  p: ({ children }: Readonly<{ children?: React.ReactNode }>) => <span>{children}</span>,
};

export interface InterviewGuideStepProps {
  clientLabel: string;
  projectAssessmentLabel: string;
  assessmentInstanceId: string;
  accessToken: string | null;
  clientId: string;
  projectId: string;
  currentFlowStep?: AssessmentFlowStep;
  onNavigateAssessmentStep: (step: AssessmentFlowStep) => void;
}

const MarkdownCard: React.FC<{ title: string; markdown: string }> = ({ title, markdown }) => (
  <Card title={title} className="interview-guide-review__card">
    <ReactMarkdown>{markdown}</ReactMarkdown>
  </Card>
);

const RoleGuideCard: React.FC<{
  guide: InterviewGuideReviewModel['roleGuides'][number];
}> = ({ guide }) => (
  <Card className="interview-guide-review__role-card">
    <Flex wrap="wrap" gap="small" justify="space-between" align="flex-start">
      <div className="interview-guide-review__role-heading">
        <Title level={4}>{guide.title}</Title>
        {guide.focus ? (
          <Text type="secondary">
            <strong>Focus:</strong> {guide.focus}
          </Text>
        ) : null}
      </div>
      <Tag color="blue">{guide.questions.length} questions</Tag>
    </Flex>
    <div className="interview-guide-review__question-list">
      {guide.questions.map((question, index) => (
        <div className="interview-guide-review__question-row" key={`${guide.title}-${index}`}>
          <span className="interview-guide-review__question-number">{index + 1}</span>
          <ReactMarkdown components={INLINE_MARKDOWN_COMPONENTS}>{question}</ReactMarkdown>
        </div>
      ))}
    </div>
  </Card>
);

const StructuredGuideContent: React.FC<{ model: InterviewGuideReviewModel }> = ({ model }) => (
  <Space direction="vertical" size="middle" className="interview-guide-review__content">
    {model.contextMarkdown ? (
      <MarkdownCard title="Context synthesis" markdown={model.contextMarkdown} />
    ) : null}
    {model.assumptionsMarkdown ? (
      <Alert
        type="warning"
        showIcon
        message="Assumptions / gaps"
        description={<ReactMarkdown>{model.assumptionsMarkdown}</ReactMarkdown>}
      />
    ) : null}
    <section className="interview-guide-review__section">
      <Title level={3}>Role-Specific Question Guides</Title>
      <Space direction="vertical" size="middle" className="interview-guide-review__content">
        {model.roleGuides.map((guide) => (
          <RoleGuideCard guide={guide} key={guide.title} />
        ))}
      </Space>
    </section>
    {model.probeBank.length > 0 ? (
      <Card title="Probe Bank - Common Responses" className="interview-guide-review__card">
        <div className="interview-guide-review__stacked-list">
          {model.probeBank.map((probe) => (
            <div className="interview-guide-review__probe-row" key={probe.response}>
              <Text strong>{probe.response}</Text>
              <Text type="secondary">{probe.probe}</Text>
            </div>
          ))}
        </div>
      </Card>
    ) : null}
    {model.redFlags.length > 0 ? (
      <Card title="Red Flags to Watch For" className="interview-guide-review__card">
        <Space direction="vertical" size="small" className="interview-guide-review__content">
          {model.redFlags.map((flag) => (
            <Alert
              key={flag.label}
              type="error"
              showIcon
              message={flag.label}
              description={flag.detail}
            />
          ))}
        </Space>
      </Card>
    ) : null}
    {model.noteCaptureTemplate ? (
      <Card title="Note-Capture Template" className="interview-guide-review__card">
        <ReactMarkdown>{model.noteCaptureTemplate}</ReactMarkdown>
      </Card>
    ) : null}
    {model.unparsedSections.map((section) => (
      <MarkdownCard key={section.title} title={section.title} markdown={section.markdown} />
    ))}
    {model.nextStep ? (
      <Alert
        type="info"
        showIcon
        message={<ReactMarkdown>{model.nextStep}</ReactMarkdown>}
        className="interview-guide-review__next-step"
      />
    ) : null}
  </Space>
);

export const InterviewGuideStep: React.FC<InterviewGuideStepProps> = ({
  clientLabel,
  projectAssessmentLabel: _projectAssessmentLabel,
  assessmentInstanceId,
  accessToken,
  clientId,
  projectId,
  currentFlowStep = '1-review',
  onNavigateAssessmentStep,
}) => {
  const editorRef = useRef<CrepeMarkdownFieldHandle | null>(null);

  const guide = useInterviewGuide({
    assessmentInstanceId,
    accessToken,
    clientId,
    projectId,
    onNavigateAssessmentStep,
  });

  const actionBar = (
    <Space wrap align="center">
      <Button
        disabled={!guide.hasMarkdown}
        onClick={() => downloadMarkdownFile(guide.visibleMarkdown, INTERVIEW_GUIDE_FILENAME)}
      >
        Download .md
      </Button>
      <Button disabled={!guide.hasMarkdown} onClick={guide.openEditor}>
        Edit
      </Button>
      <Button
        type="primary"
        disabled={!guide.hasMarkdown}
        loading={guide.isApproving}
        onClick={() => void guide.approve()}
      >
        Approve &amp; Continue
      </Button>
    </Space>
  );

  return (
    <div className="interview-guide-review">
      {/* HEADER — sticky */}
      <div className="interview-guide-review__header">
        <Flex wrap="wrap" gap="middle" align="flex-start" justify="space-between">
          <div className="interview-prep-page__hero-heading">
            <div className="interview-prep-page__step-badge" aria-hidden="true">
              1
            </div>
            <div className="interview-prep-page__hero-text">
              <Title level={2} className="interview-prep-page__hero-title">
                Interview Guide - Ready for Review
              </Title>
              <Text type="secondary" className="interview-prep-page__hero-subtitle">
                {INTERVIEW_GUIDE_FILENAME} - Generated for {clientLabel} - {guide.roleCount} roles -{' '}
                {guide.questionCount} questions
              </Text>
            </div>
          </div>
          <Space direction="vertical" align="end">
            <AssessmentStepPager
              current={currentFlowStep}
              disableNext={currentFlowStep === '1-review' && !guide.isApproved}
              onStepChange={onNavigateAssessmentStep}
            />
          </Space>
        </Flex>
      </div>

      {/* BODY — scrollable */}
      <div className="interview-guide-review__body">
        {guide.contextPackStatus === 'partial' ? (
          <Alert
            type="warning"
            showIcon
            message="Partial context pack"
            description="Some reference material was omitted or truncated because it exceeded the model input budget. Review linked assets and the stakeholder list if you need the full detail."
          />
        ) : null}

        {guide.isLoading ? <Skeleton active paragraph={{ rows: 8 }} /> : null}
        {guide.hasMarkdown && guide.parsedGuide.isStructured && !guide.isLoading ? (
          <StructuredGuideContent model={guide.parsedGuide} />
        ) : null}
        {guide.hasMarkdown && !guide.parsedGuide.isStructured && !guide.isLoading ? (
          <Space direction="vertical" size="middle" className="interview-guide-review__content">
            <Alert
              type="warning"
              showIcon
              message="Rendered as Markdown"
              description="The expected interview-guide template structure was not detected."
            />
            <MarkdownCard title="Interview Guide" markdown={guide.visibleMarkdown} />
          </Space>
        ) : null}
        {guide.isLoading || guide.hasMarkdown ? null : (
          <Card className="interview-guide-review__card">
            <Space direction="vertical" size="middle">
              <Paragraph type="secondary">
                No interview guide is stored on this assessment yet.
              </Paragraph>
              <Button type="primary" onClick={() => onNavigateAssessmentStep(1)}>
                Back to Step 1
              </Button>
            </Space>
          </Card>
        )}
      </div>

      {/* FOOTER — sticky */}
      <div className="interview-guide-review__footer">{actionBar}</div>

      <Modal
        title="Edit interview guide Markdown"
        open={guide.isEditOpen}
        onCancel={guide.closeEditor}
        onOk={() => void guide.saveEdit(editorRef.current?.getMarkdown() ?? guide.draftMarkdown)}
        okText="Save"
        confirmLoading={guide.isSaving}
        width={960}
        destroyOnHidden
      >
        <CrepeMarkdownField
          ref={editorRef}
          bootKey={`${assessmentInstanceId}-interview-guide-edit`}
          defaultMarkdown={guide.draftMarkdown}
          onMarkdownChange={guide.setDraftMarkdown}
          minHeight={520}
        />
      </Modal>
    </div>
  );
};

export default InterviewGuideStep;
